import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, LabelList, Legend
} from 'recharts';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, Box, Paper, Typography,
  Button, Card, CardContent, useTheme, alpha, Container, TextField
} from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import {
  BarChart as BarChartIcon, PieChart as PieChartIcon, People, Inventory,
  AddCircle, PersonAdd, AddShoppingCart, Dashboard as DashboardIcon
} from '@mui/icons-material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { getDashboardData, setPeriod, setDateRange } from "../../../app/store/slices/dashboardSlice";
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import AddCustomerDialog from '../../customers/components/AddCustomerDialog';
import AddProductDialog from '../../product/components/AddProductDialog';
import { toast } from 'react-toastify';

const STATUS_COLORS = ['#43a047', '#fbc02d', '#d32f2f'];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const period = useSelector(state => state.dashboard.period);
  const dashboardData = useSelector(state => state.dashboard.data);
  const dateRange = useSelector(state => state.dashboard.dateRange);
  const { storeInfo } = useStoreInfo();
  
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentQuarter, setCurrentQuarter] = useState(1);
  
  // Dialog states
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  useEffect(() => {
    // Calculate current quarter
    const now = new Date();
    const month = now.getMonth() + 1;
    const quarter = Math.ceil(month / 3);
    setCurrentQuarter(quarter);
    
    dispatch(getDashboardData({ period, dateRange }));
  }, [dispatch, period, dateRange]);

  useEffect(() => {
    // Debug: Log dashboard data when it changes
    if (dashboardData) {
    }
  }, [dashboardData]);

  const getQuarterDates = (quarter, year = new Date().getFullYear()) => {
    const quarterStartMonth = (quarter - 1) * 3;
    const startDate = new Date(year, quarterStartMonth, 1);
    const endDate = new Date(year, quarterStartMonth + 3, 0);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const handlePeriodChange = (e) => {
    const selectedPeriod = e.target.value;
    dispatch(setPeriod(selectedPeriod));
    
    const now = new Date();
    const year = now.getFullYear();
    
    let newDateRange = null;
    
    switch(selectedPeriod) {
      case 'today':
        const today = now.toISOString().split('T')[0];
        newDateRange = { startDate: today, endDate: today };
        break;
      case 'weekly':
        // Show current month from start to today, broken into weeks
        const weeklyMonthStart = new Date(year, now.getMonth(), 1);
        newDateRange = {
          startDate: weeklyMonthStart.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        };
        break;
      case 'monthly':
        const monthStart = new Date(year, now.getMonth(), 1);
        const monthEnd = new Date(year, now.getMonth() + 1, 0);
        newDateRange = {
          startDate: monthStart.toISOString().split('T')[0],
          endDate: monthEnd.toISOString().split('T')[0]
        };
        break;
      case 'monthtodate':
        const mtdStart = new Date(year, now.getMonth(), 1);
        newDateRange = {
          startDate: mtdStart.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        };
        break;
      case 'q1':
      case 'q2':
      case 'q3':
      case 'q4':
        const quarterNum = parseInt(selectedPeriod.substring(1));
        newDateRange = getQuarterDates(quarterNum, year);
        break;
      case 'ytd':
        const yearStart = new Date(year, 0, 1);
        newDateRange = {
          startDate: yearStart.toISOString().split('T')[0],
          endDate: now.toISOString().split('T')[0]
        };
        break;
      case 'lastyear':
        const lastYearStart = new Date(year - 1, 0, 1);
        const lastYearEnd = new Date(year - 1, 11, 31);
        newDateRange = {
          startDate: lastYearStart.toISOString().split('T')[0],
          endDate: lastYearEnd.toISOString().split('T')[0]
        };
        break;
      case 'custom':
        // For custom, just set period and let user enter dates in the inline fields
        // Don't set dateRange yet - wait for user to enter dates
        return;
      default:
        newDateRange = null;
    }
    
    dispatch(setDateRange(newDateRange));
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      dispatch(setDateRange({ startDate: customStartDate, endDate: customEndDate }));
    }
  };
  
  // Dialog handlers
  const handleCustomerCreated = (customer) => {
    toast.success(t("customer_created_successfully") || "Customer created successfully!");
    // Optionally navigate to customer page or refresh dashboard data
  };
  
  const handleProductCreated = (product) => {
    toast.success(t("product_created_successfully") || "Product created successfully!");
    // Optionally navigate to product page or refresh dashboard data
  };

  const salesData = useMemo(() => {
    if (!dashboardData || !dashboardData.TotalSales || dashboardData.TotalSales.length === 0) {
      return MONTHS.map(month => ({
        month,
        sales: 0,
        salesExclVAT: 0,
        vat: 0,
        salesInclVAT: 0,
      }));
    }
    
    if (period === "monthly") {
      // Show all 12 months on X-axis, map Period from API to month names
      const salesMap = {};
      dashboardData.TotalSales.forEach(item => {
        // Period contains full month name like "January", "February"
        const periodName = item.Period;
        // Map it to short month name (Jan, Feb, etc.)
        const monthIndex = new Date(Date.parse(periodName + " 1, 2026")).getMonth();
        const shortMonth = MONTHS[monthIndex];
        salesMap[shortMonth] = {
          sales: item.Sales || 0,
          salesExclVAT: item.SalesExclVAT || 0,
          vat: item.VAT || 0,
          salesInclVAT: item.SalesInclVAT || 0,
        };
      });
      return MONTHS.map(month => ({
        month,
        sales: salesMap[month]?.sales || 0,
        salesExclVAT: salesMap[month]?.salesExclVAT || 0,
        vat: salesMap[month]?.vat || 0,
        salesInclVAT: salesMap[month]?.salesInclVAT || 0,
      }));
    }
    
    if (period === "weekly") {
      // Show all weeks (W1-W5) on X-axis, Period should contain week info
      const now = new Date();
      const totalDaysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const totalWeeks = Math.ceil(totalDaysInMonth / 7);
      const WEEKS = Array.from({ length: totalWeeks }, (_, i) => `W${i + 1}`);
      
      const salesMap = {};
      dashboardData.TotalSales.forEach(item => {
        const weekKey = item.Period || item.Week;
        salesMap[weekKey] = {
          sales: item.Sales || 0,
          salesExclVAT: item.SalesExclVAT || 0,
          vat: item.VAT || 0,
          salesInclVAT: item.SalesInclVAT || 0,
        };
      });
      
      return WEEKS.map(week => ({
        week,
        sales: salesMap[week]?.sales || 0,
        salesExclVAT: salesMap[week]?.salesExclVAT || 0,
        vat: salesMap[week]?.vat || 0,
        salesInclVAT: salesMap[week]?.salesInclVAT || 0,
      }));
    }
    
    // For other periods (today, monthtodate, quarters, ytd, lastyear, custom)
    return dashboardData.TotalSales.map(item => ({
      date: item.Period || item.Date || item.Day,
      month: item.Period,
      week: item.Period,
      quarter: item.Period,
      sales: item.Sales || 0,
      salesExclVAT: item.SalesExclVAT || 0,
      vat: item.VAT || 0,
      salesInclVAT: item.SalesInclVAT || 0,
    }));
  }, [dashboardData, period]);

  const salesTotals = useMemo(() => {
    return salesData.reduce(
      (acc, item) => ({
        salesExclVAT: acc.salesExclVAT + (item.salesExclVAT || 0),
        vat: acc.vat + (item.vat || 0),
        salesInclVAT: acc.salesInclVAT + (item.salesInclVAT || 0),
      }),
      { salesExclVAT: 0, vat: 0, salesInclVAT: 0 }
    );
  }, [salesData]);

  const invoiceStatusData = useMemo(() => {
    if (!dashboardData || !dashboardData.InvoicesStatus || dashboardData.InvoicesStatus.length === 0) {
      return [{ name: "No Data", value: 1 }];
    }
    return dashboardData.InvoicesStatus.map(item => ({
      name: item.Name,
      value: item.Value,
    }));
  }, [dashboardData]);

  const topCustomersData = useMemo(() => {
    if (!dashboardData || !dashboardData.TopCustomers || dashboardData.TopCustomers.length === 0) {
      return [{ name: "", amount: 0 }];
    }
    return dashboardData.TopCustomers.map(item => ({
      name: item.Name,
      amount: item.Amount,
    }));
  }, [dashboardData]);

  const topSellingProductsData = useMemo(() => {
    if (!dashboardData || !dashboardData.TopSellingProducts || dashboardData.TopSellingProducts.length === 0) {
      return [{ name: "", sold: 0 }];
    }
    return dashboardData.TopSellingProducts.map(item => ({
      name: item.Name,
      sold: item.Sold,
    }));
  }, [dashboardData]);

  const getXAxisKey = () => {
    if (salesData.length === 0) return "month";
    
    // Determine X-axis based on period selection
    switch(period) {
      case 'today':
      case 'monthtodate':
      case 'custom':
        return 'date'; // Show dates on X-axis for month-to-date
      case 'weekly':
        return 'week'; // Show weeks on X-axis
      case 'monthly':
        return 'month'; // Show months on X-axis for monthly
      case 'q1':
      case 'q2':
      case 'q3':
      case 'q4':
        return 'quarter'; // Show quarters on X-axis for quarterly
      case 'ytd':
      case 'lastyear':
        return 'month'; // Show months on X-axis for yearly
      default:
        const keys = Object.keys(salesData[0]);
        return keys.find(k => k !== "sales") || "month";
    }
  };

  const getBarSize = (data) => (data.length === 1 ? 32 : 18);

  const renderSalesTotalLabel = ({ x, y, width, height, payload }) => {
    const value = payload?.salesInclVAT;
    if (!value) return null;
    return (
      <text
        x={x + width + 18}
        y={y + height / 2}
        fill={theme.palette.success.main}
        fontWeight={600}
        fontSize={13}
        dominantBaseline="middle"
      >
        €{value}
      </text>
    );
  };

  const renderSalesTotalLabelEnd = ({ x, y, width, height, payload, value: barValue }) => {
    const total = payload?.salesInclVAT;
    if (!total || total === 0) return null;
    
    // Calculate the actual end position of the stacked bar
    const salesExclVAT = payload?.salesExclVAT || 0;
    const vat = payload?.vat || 0;
    const stackedWidth = (salesExclVAT + vat) / total * width;
    const textX = x + stackedWidth + 5;
    
    return (
      <g>
        <rect
          x={textX - 2}
          y={y + height / 2 - 10}
          width={50}
          height={20}
          fill="#fff"
          stroke={theme.palette.success.main}
          strokeWidth={2}
          rx={4}
        />
        <text
          x={textX + 23}
          y={y + height / 2}
          fill={theme.palette.success.main}
          fontWeight={700}
          fontSize={12}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          €{total}
        </text>
      </g>
    );
  };

  const totalSalesAmount = useMemo(() => {
    // Total Sales excluding VAT
    return dashboardData?.TotalSalesAmount || 0;
  }, [dashboardData]);

  const totalSalesAmountInclVAT = useMemo(() => {
    // Total Sales including VAT
    return dashboardData?.TotalSalesAmountInclVAT || 0;
  }, [dashboardData]);

  const totalInvoices = useMemo(() => {
    return invoiceStatusData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [invoiceStatusData]);

  const totalVAT = useMemo(() => {
    return dashboardData?.TotalVAT || 0;
  }, [dashboardData]);

  const periodLabel = {
    today: t("today"),
    weekly: t("weekly"),
    monthly: t("monthly"),
    monthtodate: t("month_to_date") || "Month to Date",
    q1: t("q1") || "Q1",
    q2: t("q2") || "Q2",
    q3: t("q3") || "Q3",
    q4: t("q4") || "Q4",
    ytd: t("year_to_date") || "Year to Date",
    lastyear: t("last_year") || "Last Year",
    custom: t("custom_date") || "Custom Date",
    annually: t("annually"),
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
            <DashboardIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" color="primary.main">
                {t("dashboard")}
                {storeInfo && storeInfo.name && (
                  <Typography component="span" variant="h6" fontWeight="500" color="text.secondary" sx={{ ml: 1 }}>
                    - {storeInfo.name}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {storeInfo?._raw?.StoreName && storeInfo?._raw?.CompanyName && storeInfo._raw.StoreName !== storeInfo._raw.CompanyName
                  ? `${storeInfo._raw.StoreName} | ${storeInfo._raw.CompanyName}`
                  : storeInfo?.name || t("welcome_dashboard")}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ 
              minWidth: 200, 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2,
                backgroundColor: '#fff'
              } 
            }}>
              <InputLabel id="dashboard-period-label">{t("period")}</InputLabel>
              <Select
                labelId="dashboard-period-label"
                value={period}
                label={t("period")}
                onChange={handlePeriodChange}
              >
                <MenuItem value="today">{t("today")}</MenuItem>
                <MenuItem value="weekly">{t("weekly")}</MenuItem>
                <MenuItem value="monthly">{t("monthly")}</MenuItem>
                <MenuItem value="monthtodate">{t("month_to_date") || "Month to Date"}</MenuItem>
                {currentQuarter >= 1 && <MenuItem value="q1">{t("q1") || "Q1"}</MenuItem>}
                {currentQuarter >= 2 && <MenuItem value="q2">{t("q2") || "Q2"}</MenuItem>}
                {currentQuarter >= 3 && <MenuItem value="q3">{t("q3") || "Q3"}</MenuItem>}
                {currentQuarter >= 4 && <MenuItem value="q4">{t("q4") || "Q4"}</MenuItem>}
                <MenuItem value="ytd">{t("year_to_date") || "Year to Date"}</MenuItem>
                <MenuItem value="lastyear">{t("last_year") || "Last Year"}</MenuItem>
                <MenuItem value="custom">{t("custom_date") || "Custom Date"}</MenuItem>
              </Select>
            </FormControl>
            
            {period === 'custom' && (
              <>
                <TextField
                  size="small"
                  label={t("start_date") || "From"}
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  onBlur={handleCustomDateApply}
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    minWidth: 160,
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                />
                <TextField
                  size="small"
                  label={t("end_date") || "To"}
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  onBlur={handleCustomDateApply}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: customStartDate }}
                  sx={{ 
                    minWidth: 160,
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      backgroundColor: '#fff'
                    }
                  }}
                />
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate('/tickets/add')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.info.main,
              '&:hover': {
                backgroundColor: theme.palette.info.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
            }}
          >
            {t("ticket_de_caisse")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircle />}
            onClick={() => navigate('/invoices/add')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.success.main,
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(56, 142, 60, 0.3)'
            }}
          >
            {t("add_invoice")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<PersonAdd />}
            onClick={() => setCustomerDialogOpen(true)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}
          >
            {t("add_customer")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddShoppingCart />}
            onClick={() => setProductDialogOpen(true)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
            }}
          >
            {t("add_product")}
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {t("total_sale_excl_vat") || "Total Sale (Excl VAT)"}
                </Typography>
                <Typography variant="h4" fontWeight="700">
                  €{totalSalesAmount.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {periodLabel[period]}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {t("total_vat") || t("total_tva") || t("total_btw") || "Total VAT"}
                </Typography>
                <Typography variant="h4" fontWeight="700">
                  €{totalVAT.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {periodLabel[period]}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {t("total_sale_incl_vat") || "Total Sale (Incl VAT)"}
                </Typography>
                <Typography variant="h4" fontWeight="700">
                  €{totalSalesAmountInclVAT.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {periodLabel[period]}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden', 
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`
            }}>
              <BarChartIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("totalSales")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 420, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={salesData}
                  layout="vertical"
                  barCategoryGap={salesData.length === 1 ? "20%" : "12%"}
                  margin={{ top: 30, right: 140, left: 20, bottom: 10 }}
                >
                  <XAxis type="number" />
                  <YAxis type="category" dataKey={getXAxisKey()} width={110} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const labelMap = {
                        salesExclVAT: t("total_sale_excl_vat") || "Sales (Excl VAT)",
                        vat: t("total_vat") || t("total_tva") || t("total_btw") || "VAT",
                        salesInclVAT: t("total_sale_incl_vat") || "Sales (Incl VAT)",
                        sales: t("sales"),
                      };
                      
                      // Get the actual data values
                      const dataPoint = payload[0]?.payload;
                      const items = [
                        {
                          key: 'salesExclVAT',
                          name: labelMap['salesExclVAT'],
                          value: dataPoint?.salesExclVAT || 0,
                          color: theme.palette.primary.main,
                        },
                        {
                          key: 'vat',
                          name: labelMap['vat'],
                          value: dataPoint?.vat || 0,
                          color: theme.palette.warning.main,
                        },
                        {
                          key: 'salesInclVAT',
                          name: labelMap['salesInclVAT'],
                          value: dataPoint?.salesInclVAT || 0,
                          color: theme.palette.success.main,
                        }
                      ];

                      return (
                        <Box
                          sx={{
                            backgroundColor: '#fff',
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            p: 1.5,
                            border: '1px solid rgba(0,0,0,0.08)'
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {label}
                          </Typography>
                          {items.map(item => (
                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {item.name}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                €{item.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: 8 }}
                    formatter={(value) => {
                      const labelMap = {
                        salesExclVAT: t("total_sale_excl_vat") || "Sales (Excl VAT)",
                        vat: t("total_vat") || t("total_tva") || t("total_btw") || "VAT",
                        salesInclVAT: t("total_sale_incl_vat") || "Sales (Incl VAT)",
                        sales: t("sales"),
                      };
                      return labelMap[value] || value;
                    }}
                  />
                  <Bar
                    dataKey="salesExclVAT"
                    stackId="sales"
                    fill={theme.palette.primary.main}
                    barSize={getBarSize(salesData)}
                    name={t("total_sale_excl_vat") || "Sales (Excl VAT)"}
                    minPointSize={6}
                  >
                    <LabelList
                      dataKey="salesExclVAT"
                      position="insideRight"
                      fill="#fff"
                      fontSize={12}
                      formatter={(value) => (value ? `€${value}` : '')}
                    />
                  </Bar>
                  <Bar
                    dataKey="vat"
                    stackId="sales"
                    fill={theme.palette.warning.main}
                    barSize={getBarSize(salesData)}
                    name={t("total_vat") || t("total_tva") || t("total_btw") || "VAT"}
                    minPointSize={6}
                  >
                    <LabelList
                      dataKey="vat"
                      position="insideRight"
                      fill="#fff"
                      fontSize={12}
                      formatter={(value) => (value ? `€${value}` : '')}
                    />
                    <LabelList content={renderSalesTotalLabel} />
                  </Bar>
                  <Bar dataKey="salesInclVAT" stackId="labelLayer" fill="none" barSize={0}>
                    <LabelList content={renderSalesTotalLabelEnd} />
                  </Bar>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
              {salesData.every(item => (item.salesExclVAT || 0) === 0 && (item.vat || 0) === 0 && (item.salesInclVAT || 0) === 0) && (
                <Box sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18,
                  fontWeight: 500,
                  zIndex: 2,
                  pointerEvents: "none"
                }}>
                  {t("noSales")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden', 
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`
            }}>
              <PieChartIcon sx={{ color: theme.palette.info.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("invoicesStatus")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              {invoiceStatusData.length === 0 ? (
                <Box sx={{
                  height: '100%',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18
                }}>
                  {t("noInvoicesStatus")}
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={invoiceStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      paddingAngle={4}
                      dataKey="value"
                      labelLine={true}
                      label={({ cx, cy, midAngle, outerRadius, name, percent }) => {
                        const RADIAN = Math.PI / 180;
                        const radius = outerRadius + 25;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text 
                            x={x} 
                            y={y} 
                            fill="#333" 
                            textAnchor={x > cx ? 'start' : 'end'} 
                            dominantBaseline="central"
                            fontSize="12"
                            fontWeight="500"
                          >
                            {`${name} ${(percent * 100).toFixed(0)}%`}
                          </text>
                        );
                      }}
                    >
                      {invoiceStatusData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[index % STATUS_COLORS.length]} 
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} ${t("invoices")}`, name]}
                      contentStyle={{
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: 'none'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.15)} 100%)`
            }}>
              <People sx={{ color: theme.palette.warning.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("topCustomers")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topCustomersData}
                  layout="vertical"
                  barCategoryGap={topCustomersData.length === 1 ? "60%" : "10%"}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                >
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`€${value}`, t("sales")]}
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  />
                  <Bar
                    dataKey="amount"
                    fill="url(#customerGradient)"
                    barSize={getBarSize(topCustomersData)}
                    radius={[0, 4, 4, 0]}
                    label={{
                      position: "right",
                      fill: theme.palette.warning.dark,
                      fontWeight: 600,
                      fontSize: 12,
                      formatter: (value) => `€${value}`
                    }}
                  />
                  <defs>
                    <linearGradient id="customerGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={theme.palette.warning.main} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={theme.palette.warning.main} stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
              {topCustomersData.every(item => item.amount === 0) && (
                <Box sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18,
                  fontWeight: 500,
                  zIndex: 2,
                  pointerEvents: "none"
                }}>
                  {t("noTopCustomers")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`
            }}>
              <Inventory sx={{ color: theme.palette.success.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("topProducts")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topSellingProductsData}
                  layout="vertical"
                  barCategoryGap={topSellingProductsData.length === 1 ? "60%" : "10%"}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                >
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value} ${t("products")}`, t("sales")]}
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  />
                  <Bar
                    dataKey="sold"
                    fill="url(#productGradient)"
                    barSize={getBarSize(topSellingProductsData)}
                    radius={[0, 4, 4, 0]}
                    label={{
                      position: "right",
                      fill: theme.palette.success.dark,
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                  <defs>
                    <linearGradient id="productGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={theme.palette.success.main} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={theme.palette.success.main} stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
              {topSellingProductsData.every(item => item.sold === 0) && (
                <Box sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18,
                  fontWeight: 500,
                  zIndex: 2,
                  pointerEvents: "none"
                }}>
                  {t("noTopProducts")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onCustomerCreated={handleCustomerCreated}
      />
      
      {/* Add Product Dialog */}
      <AddProductDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        onProductCreated={handleProductCreated}
      />
    </Container>
  );
};

export default Dashboard;